import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { CheckCircle2, Download, ArrowRight, Headphones, Target, Calendar, Users } from 'lucide-react';
import { CALENDLY_URL } from '../lib/siteConfig';

// Brand-aligned palette — matches the marketing site (teal/cyan/cream).
const NAVY = '#004C54';
const GOLD = '#4FBCC7';
const CREAM = '#F5F3EE';

// Audience-specific copy + quiz deep-link, keyed by the PDF filename that the
// lead-magnet pages pass in via `?pdf=...`. This makes the same thank-you
// route work for all three lead magnets without losing the audience signal.
const AUDIENCE_BY_PDF = {
  '/car-ride-home-playbook.pdf': {
    key: 'parent',
    label: 'Parent',
    quizCta: 'Take the 2-Min Parenting Style Quiz',
    quizHref: '/quiz?audience=parent',
    deeperPitch:
      "The Playbook gives you the phrases that work. The 2-minute Parenting Style Quiz tells you " +
      "WHICH ones to lead with based on your default style — Fixer, Cheerleader, Hoverer, or Stoic.",
    nextStep: {
      eyebrow: 'Talk it through · 30 min',
      title: 'Bring your scenarios to Coach Lewis',
      body: "Specific car-ride moments, big-game weeks, the conversations you keep getting wrong. We'll map the language together.",
      cta: 'Book a Discovery Call',
      href: CALENDLY_URL,
      external: true,
      icon: Calendar,
      label: 'Discovery Call',
    },
  },
  '/pre-game-routine.pdf': {
    key: 'athlete',
    label: 'Athlete',
    quizCta: 'Take the 2-Min Athlete Mindset Quiz',
    quizHref: '/quiz?audience=athlete',
    deeperPitch:
      "The routine works for everyone. The 2-minute Athlete Mindset Quiz tells you which of the " +
      "Apex Six frameworks to build your week around — based on your archetype.",
    nextStep: {
      eyebrow: 'Next level · $27',
      title: 'Get your full Apex Six IDP',
      body: 'A 6-dimension scored profile with your Primary Action, the script, and a printable PDF. The pre-game routine is the warm-up; the IDP is the playbook.',
      cta: 'Start my IDP — $27',
      href: '/assessment',
      external: false,
      icon: Target,
      label: 'IDP',
    },
  },
  '/coaches-reset.pdf': {
    key: 'coach',
    label: 'Coach',
    quizCta: 'Take the 2-Min Coaching Style Quiz',
    quizHref: '/quiz?audience=coach',
    deeperPitch:
      "The Reset works for every coach. The 2-minute Coaching Style Quiz tells you which protocol " +
      "to layer in next based on whether you're a Drill Sergeant, Mentor, Tactician, or Player's Coach.",
    nextStep: {
      eyebrow: 'Team workshop · Scoped on a Planning Call.',
      title: 'Bring the Apex Six to your roster',
      body: '60–90 min in-person or virtual session for your team. Composure, confidence, and pressure-response drills your players will use in the next game.',
      cta: 'Inquire about a Workshop',
      href: CALENDLY_URL,
      external: true,
      icon: Users,
      label: 'Workshop',
    },
  },
};

const DEFAULT_AUDIENCE = {
  key: 'generic',
  label: '',
  quizCta: 'Take the 2-Min Mindset Quiz',
  quizHref: '/quiz',
  deeperPitch:
    "The playbook is just the start. Take the 2-minute Mindset Quiz to find the #1 mental gap costing " +
    "your athlete reps, at-bats, and confidence.",
  nextStep: {
    eyebrow: 'Free · while you are here',
    title: 'Drop into The Locker Room',
    body: 'Pre-game playlists + the Apex Six notecards. Free to enter — built for athletes who want to feel ready.',
    cta: 'Open The Locker Room',
    href: '/locker-room',
    external: false,
    icon: Headphones,
    label: 'Locker Room',
  },
};

function trackThankYouCta(label, audienceKey) {
  try {
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('event', 'thankyou_cta_click', { label, audience: audienceKey });
    }
  } catch {
    /* analytics is best-effort */
  }
}

export default function PlaybookThankYou() {
  const [params] = useSearchParams();
  const pdfUrl = params.get('pdf') || '/car-ride-home-playbook.pdf';
  const audience = AUDIENCE_BY_PDF[pdfUrl] || DEFAULT_AUDIENCE;

  return (
    <div className="min-h-screen" style={{ background: CREAM }} data-testid="playbook-thankyou-page">
      <div style={{ background: NAVY, color: 'rgba(255,255,255,0.8)' }} className="text-[12px]">
        <div className="max-w-[1120px] mx-auto px-5 py-2">
          <Link to="/" className="font-mono-accent uppercase tracking-[0.18em] hover:text-white" data-testid="playbook-thankyou-home">
            ← apexathleticmind.com
          </Link>
        </div>
      </div>

      <div className="max-w-[640px] mx-auto px-5 sm:px-8 py-14 sm:py-20">
        <div className="text-center mb-10">
          <div className="w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-5" style={{ background: NAVY }}>
            <CheckCircle2 className="h-8 w-8" style={{ color: GOLD }} />
          </div>
          <div className="font-mono-accent uppercase tracking-[0.24em] text-[10px] font-bold mb-3" style={{ color: GOLD }} data-testid="playbook-thankyou-eyebrow">
            Your playbook is on its way
          </div>
          <h1 className="font-heading text-[2rem] sm:text-[2.5rem] font-bold tracking-tight leading-tight mb-4" style={{ color: NAVY }} data-testid="playbook-thankyou-title">
            Your playbook is on its way.
          </h1>
          <p className="text-[15.5px] leading-relaxed text-[#3a3a3a] max-w-[460px] mx-auto" data-testid="playbook-thankyou-sub">
            Check your email — the PDF download link is in your inbox right now. If you don’t see it within a couple
            of minutes, check spam.
          </p>
        </div>

        <div
          className="rounded-2xl shadow-[0_4px_20px_rgba(0,76,84,0.07)] p-7 sm:p-8 border bg-white"
          style={{ borderColor: 'rgba(0,76,84,0.1)' }}
        >
          <h2 className="font-heading text-[18px] font-bold mb-4" style={{ color: NAVY }}>
            Or download it right now:
          </h2>
          <Button
            asChild
            className="w-full font-bold h-12 mb-4"
            style={{ background: NAVY, color: 'white' }}
            data-testid="playbook-thankyou-download"
          >
            <a href={pdfUrl} target="_blank" rel="noreferrer" download>
              <Download className="h-4 w-4 mr-2" /> Download Now (PDF)
            </a>
          </Button>

          <div className="my-7 border-t" style={{ borderColor: 'rgba(0,76,84,0.1)' }} />

          <h3
            className="font-heading text-[16px] font-bold mb-2"
            style={{ color: NAVY }}
            data-testid={`playbook-thankyou-deeper-${audience.key}`}
          >
            Want to go deeper?
          </h3>
          <p className="text-[13.5px] text-[#3a3a3a] leading-relaxed mb-4">
            {audience.deeperPitch}
          </p>
          <Button
            asChild
            variant="outline"
            className="w-full font-bold h-12 border-2"
            style={{ borderColor: NAVY, color: NAVY, background: 'white' }}
            data-testid="playbook-thankyou-quiz-cta"
          >
            <Link to={audience.quizHref} onClick={() => trackThankYouCta('Quiz', audience.key)}>
              {audience.quizCta} <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>

        {/*
          Audience-aware paid next-step CTA — Phase 21.4
          Athlete PDF → $27 IDP / Parent PDF → Discovery Call / Coach PDF →
          Workshop inquiry / generic → Locker Room (free). Sits below the
          quiz CTA so the free option remains the first ask.
        */}
        {audience.nextStep && (
          <div
            className="mt-6 rounded-2xl p-6 sm:p-7 text-white shadow-[0_8px_28px_rgba(0,76,84,0.18)]"
            style={{ background: `linear-gradient(135deg, ${NAVY} 0%, #003a40 100%)` }}
            data-testid={`playbook-thankyou-nextstep-${audience.key}`}
          >
            <div
              className="inline-flex items-center gap-1.5 font-mono-accent uppercase tracking-[0.22em] text-[10px] font-bold mb-2"
              style={{ color: GOLD }}
            >
              {React.createElement(audience.nextStep.icon, { className: 'h-3 w-3' })}
              {audience.nextStep.eyebrow}
            </div>
            <h3 className="font-heading text-[1.25rem] sm:text-[1.4rem] font-bold tracking-tight leading-[1.15] mb-2">
              {audience.nextStep.title}
            </h3>
            <p className="text-[13.5px] text-white/75 leading-snug mb-4 max-w-[44ch]">
              {audience.nextStep.body}
            </p>
            <Button
              asChild
              className="font-bold h-11 px-5"
              style={{ background: 'white', color: NAVY }}
              data-testid="playbook-thankyou-nextstep-cta"
            >
              {audience.nextStep.external ? (
                <a
                  href={audience.nextStep.href}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => trackThankYouCta(audience.nextStep.label, audience.key)}
                >
                  {audience.nextStep.cta} <ArrowRight className="ml-2 h-4 w-4" />
                </a>
              ) : (
                <Link
                  to={audience.nextStep.href}
                  onClick={() => trackThankYouCta(audience.nextStep.label, audience.key)}
                >
                  {audience.nextStep.cta} <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              )}
            </Button>
          </div>
        )}

        <p className="text-center mt-8 text-[12.5px] leading-relaxed" style={{ color: 'rgba(0,76,84,0.55)' }}>
          Questions? Just reply to your confirmation email — Coach Lewis personally reads every reply.
        </p>
      </div>
    </div>
  );
}
